import React, { useState } from 'react';
import InfoPopover from '../InfoPopover';
import './MetricsRow.css';

/**
 * MetricsRow - Compact horizontal display of key performance metrics
 *
 * Design: Value on top (large), label below (small)
 * Primary metrics: Total Return, Max Drawdown, Annualized Return (CAGR)
 * Secondary metrics (hidden by default): Sharpe, Sortino, ROMAD
 *
 * @param {Object} metrics - Object containing metric values
 * @param {boolean} compact - Use smaller sizing
 * @param {boolean} showToggle - Show the "More Stats" toggle button
 * @param {boolean} showSecondary - Show secondary metrics (only when showToggle is false)
 */
export default function MetricsRow({
  metrics = {},
  compact = false,
  showToggle = true,
  showSecondary = false,
  size = 'default',
}) {
  const [expanded, setExpanded] = useState(false);

  const formatMoney = (value) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      : null;
  };

  const metricExplanations = {
    totalReturn: {
      meaning:
        'The percentage change in account value over this exact backtest window (ending vs starting cash).',
      formula: '(ending value − starting value) ÷ starting value',
    },
    maxDrawdown: {
      meaning: 'The largest peak-to-trough decline in the equity curve during this run.',
      formula: '(trough value − prior peak value) ÷ prior peak value',
    },
    annualReturn: {
      meaning: 'The compounded yearly rate implied by the return over this backtest window.',
      formula: '(ending value ÷ starting value)^(365 ÷ elapsed days) − 1',
    },
  };

  const metricPopover = (key, label) => {
    const detail = metrics.metricDetails?.[key] || {};
    const startValue = formatMoney(detail.startValue);
    const endValue = formatMoney(detail.endValue);
    const startDate = detail.startDate ? new Date(detail.startDate) : null;
    const endDate = detail.endDate ? new Date(detail.endDate) : null;
    const elapsedDays =
      startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())
        ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000))
        : null;
    return (
      <InfoPopover label={label}>
        <p>{metricExplanations[key].meaning}</p>
        <p>
          <strong>Formula:</strong> <code>{metricExplanations[key].formula}</code>
        </p>
        {(startValue || endValue || detail.startDate || detail.endDate) && (
          <p>
            <strong>This run:</strong>{' '}
            {[startValue, detail.startDate, endValue, detail.endDate].filter(Boolean).join(' · ')}
          </p>
        )}
        {detail.sourceArtifact && (
          <p>
            <strong>Source:</strong> {detail.sourceArtifact}
          </p>
        )}
        {key === 'totalReturn' &&
          detail.cashFlowAdjustedReturn !== undefined &&
          detail.cashFlowAdjustedReturn !== null && (
            <p>
              <strong>Cash-flow-adjusted (secondary):</strong>{' '}
              {formatPercent(detail.cashFlowAdjustedReturn)}
              {detail.cashFlowAdjustedSourceArtifact
                ? ` · ${detail.cashFlowAdjustedSourceArtifact}`
                : ''}
              . This compounds period returns after removing deposits/withdrawals and can differ
              from the balance change above.
            </p>
          )}
        {key === 'annualReturn' && elapsedDays !== null && elapsedDays < 365 && (
          <p className="info-popover__warning">
            This is a short window ({elapsedDays} days). Annualizing compounds that result
            hypothetically; it is not a forecast.
          </p>
        )}
      </InfoPopover>
    );
  };

  // Format percentage: 0.1523 -> "+15.23%", 49.9168 -> "+4,991.68%"
  const formatPercent = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    // Convert to number in case it's a string from API
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return 'N/A';
    }
    const pct = numValue * 100;
    const sign = pct >= 0 ? '+' : '-';
    // Use toLocaleString for thousands separator (e.g., 4991.68 -> 4,991.68)
    const formatted = Math.abs(pct).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign}${formatted}%`;
  };

  // Format ratio: 1.85 -> "1.85"
  const formatRatio = (value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    // Convert to number in case it's a string from API
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return 'N/A';
    }
    return numValue.toFixed(2);
  };

  // Get value class for coloring
  const getValueClass = (value, alwaysNegative = false) => {
    if (value === null || value === undefined) {
      return '';
    }
    // Convert to number in case it's a string from API
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }
    if (alwaysNegative) return numValue === 0 ? 'neutral' : 'negative';
    return numValue >= 0 ? 'positive' : 'negative';
  };

  const primaryMetrics = [
    {
      label: 'Total Return',
      key: 'totalReturn',
      value: formatPercent(metrics.totalReturn),
      className: getValueClass(metrics.totalReturn),
    },
    {
      label: 'Max Drawdown',
      key: 'maxDrawdown',
      value: formatPercent(metrics.maxDrawdown),
      className: getValueClass(metrics.maxDrawdown, true),
    },
    {
      label: 'Annualized Return (CAGR)',
      key: 'annualReturn',
      value: formatPercent(metrics.annualReturn),
      className: getValueClass(metrics.annualReturn),
    },
  ];

  const secondaryMetrics = [
    {
      label: 'Sharpe',
      value: formatRatio(metrics.sharpe),
      className: getValueClass(metrics.sharpe),
    },
    {
      label: 'Sortino',
      value: formatRatio(metrics.sortino),
      className: getValueClass(metrics.sortino),
    },
    {
      label: 'ROMAD',
      value: formatRatio(metrics.romad),
      className: getValueClass(metrics.romad),
    },
  ];

  const shouldShowSecondary = showToggle ? expanded : showSecondary;

  return (
    <div
      className={[
        'metrics-row',
        compact ? 'metrics-row--compact' : '',
        size !== 'default' ? `metrics-row--${size}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="metrics-row__primary">
        {primaryMetrics.map((metric) => (
          <div key={metric.label} className="metrics-row__metric">
            <span className={`metrics-row__value ${metric.className}`}>{metric.value}</span>
            <span className="metrics-row__label">
              <span className="metrics-row__label-text">{metric.label}</span>
              {metricPopover(metric.key, metric.label)}
            </span>
          </div>
        ))}
      </div>

      {shouldShowSecondary && (
        <div className="metrics-row__secondary">
          {secondaryMetrics.map((metric) => (
            <div key={metric.label} className="metrics-row__metric metrics-row__metric--secondary">
              <span className={`metrics-row__value ${metric.className}`}>{metric.value}</span>
              <span className="metrics-row__label">{metric.label}</span>
            </div>
          ))}
        </div>
      )}

      {showToggle && (
        <button
          className="metrics-row__toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Less' : 'More Stats'}
        </button>
      )}
    </div>
  );
}
